
import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Bell, Building, ArrowRight, LayoutDashboard, CalendarRange } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import OrganizerProfileForm from '@/components/profile/OrganizerProfileForm';
import CompanyInfoForm from '@/components/profile/CompanyInfoForm';
import ProfileTab from '@/components/profile/ProfileTab';
import { useOrganizerProfile } from '@/hooks/useOrganizerProfile';
import { useAuth } from '@/contexts/auth/AuthContext';
import ProfileSidebar from '@/components/profile/ProfileSidebar';
import { toast } from '@/hooks/use-toast';
import { ProfileUpdateData, NotificationPreferences as NotificationPrefsType } from '@/contexts/auth/types';
import NotificationPreferences from '@/components/notification/NotificationPreferences';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { isOrganizerProfileComplete } from '@/utils/profileUtils';

const OrganizerProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as {
    defaultTab?: string;
    _ts?: number;
    requireProfileCompletion?: boolean;
  } | null;
  const [activeTab, setActiveTab] = useState<string>(locationState?.defaultTab || 'profile');
  const [showCompletionAlert, setShowCompletionAlert] = useState<boolean>(
    locationState?.requireProfileCompletion || false
  );

  const { user, updateProfile } = useAuth();
  const {
    profileData,
    isLoading,
    isLoadingProfile,
    handleProfileSubmit,
  } = useOrganizerProfile();

  // Effect to handle navigation after successful profile update
  useEffect(() => {
    if (user && isOrganizerProfileComplete(user) && showCompletionAlert) {
      // Get stored redirect or default to dashboard
      const redirectPath = localStorage.getItem('redirect-after-profile') || '/organizer/dashboard';

      // Clear the storage item
      localStorage.removeItem('redirect-after-profile');

      // Show success message
      toast({
        title: "Profil sikeresen kitöltve",
        description: "Köszönjük! Most már folytathatja a munkát.",
      });

      // Hide the alert
      setShowCompletionAlert(false);
    }
  }, [user, showCompletionAlert]);

  useEffect(() => {
    if (locationState?.defaultTab) {
      setActiveTab(locationState.defaultTab);
    }
  }, [locationState?.defaultTab, locationState?._ts]);

  if (!user) {
    return null;
  }

  const handleNotificationPreferencesChange = async (preferences: NotificationPrefsType) => {
    if (!user) return;

    const updateData: ProfileUpdateData = {
      owner: 'me',
      notificationPreferences: preferences
    };

    const success = await updateProfile(updateData);

    if (success) {
      toast({
        title: "Értesítési beállítások frissítve",
        description: "Az Ön értesítési beállításai sikeresen frissítve lettek.",
        variant: "default",
      });
    }
  };

  const getDisplayName = () => {
    if (!user) return '';
    const { firstName, lastName } = user.userProfile.name;
    return `${lastName} ${firstName}`;
  };

  if (isLoadingProfile) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-medihub-primary" />
            <p className="mt-4 text-gray-500">Profil adatok betöltése...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleNavigate = (path: string) => {
    if (showCompletionAlert && !isOrganizerProfileComplete(user)) {
      toast({
        title: "Profil kitöltése szükséges",
        description: "Kérjük, töltse ki a kötelező profil adatokat a folytatáshoz.",
        variant: "destructive",
      });
      return;
    }
    navigate(path);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-32">
      <div className="lg:col-span-1">
        <ProfileSidebar
          name={getDisplayName()}
          email={profileData.email}
          contactPhone={profileData.contactPhone}
          website={profileData.website}
          companyName={user?.userProfile?.companyInfo?.name}
          companyInfo={user?.userProfile?.companyInfo}
        />
      </div>
      <div className="lg:col-span-3">
        {/* Navigation Card */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 justify-between items-center">
              <h2 className="text-xl font-semibold">Szervezői funkciók</h2>
              <div className="flex gap-3">
                <Button
                  variant="default"
                  className="flex items-center gap-2"
                  onClick={() => handleNavigate('/organizer/dashboard')}
                >
                  <CalendarRange className="h-4 w-4" />
                  Események kezelése
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Completion Alert */}
        {showCompletionAlert && (
          <Alert className="mb-6 border-amber-500 bg-amber-50">
            <AlertTitle className="text-amber-800">Profil kitöltése szükséges</AlertTitle>
            <AlertDescription className="text-amber-700">
              A rendszer használatához kérjük, töltse ki a profil adatait és a céges információkat.
              Ezek szükségesek az események és számlázási adatok kezeléséhez.
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Profil adatok
            </TabsTrigger>
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Céges adatok
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Értesítések
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab
              user={user}
              displayName={getDisplayName()}
              email={user?.email}
              role={user?.roles[0]}
              contactPhone={profileData.contactPhone}
              website={profileData.website}
              notificationPreferences={user.userProfile.notificationPreferences}
              companyName={user?.userProfile?.companyInfo?.name}
            >
              <OrganizerProfileForm
                firstName={profileData.firstName}
                lastName={profileData.lastName}
                academicTitle={profileData.academicTitle}
                formalSalutation={profileData.formalSalutation}
                contactPhone={profileData.contactPhone}
                isLoading={isLoading}
                onSubmit={handleProfileSubmit}
              />
            </ProfileTab>
          </TabsContent>

          <TabsContent value="company">
            <ProfileTab
              user={user}
              displayName={getDisplayName()}
              email={user?.email}
              role={user?.roles[0]}
              contactPhone={profileData.contactPhone}
              website={profileData.website}
              notificationPreferences={user.userProfile.notificationPreferences}
              companyName={user?.userProfile?.companyInfo?.name}
              companyInfo={user?.userProfile?.companyInfo}
            >
              <CompanyInfoForm />
            </ProfileTab>
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationPreferences
              preferences={user.userProfile.notificationPreferences || {} as NotificationPrefsType}
              onChange={handleNotificationPreferencesChange}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default OrganizerProfile;
